import { ReactNode } from 'react';

import { DenialCheckAction } from 'models/DenialCheckAction';
import { SexOffenderCheckAction } from 'models/SexOffenderCheckAction';
import { SyncPatientToEhrAction } from 'models/SyncPatientToEhrAction';
import Workflow from 'models/Workflow';
import { WorkflowAction } from 'models/WorkflowAction';

import DenialCheckReview, { DenialCheckSummary } from '../../DenialCheckReview';
import DuplicatePatientReview, { DuplicateLinkNote } from '../../DuplicatePatientReview';
import ExtractionResults from '../../ExtractionResults';
import ProcessAttachmentsNote from '../../ProcessAttachmentsNote';
import ReferralOutcomePanel, { ReferralOutcomeResult } from '../../ReferralOutcomePanel';
import SexOffenderCheckReview, { SexOffenderCheckSummary } from '../../SexOffenderCheckReview';
import SyncPatientBody from '../../syncPatient/SyncPatientBody';
import { ActionHandlers } from '../handlers';
import { CompletedNote, InlineLink } from '../styles';

/**
 * Per-kind card behavior, one entry per kind that needs any (OLIO-2700).
 *
 * A kind absent from here is not special: it gets the generic body and no slideout. Adding a kind
 * means adding one entry, not editing a branch in the chrome, the row map, and a predicate.
 */
// What every renderer gets. Handlers are passed whole rather than one callback at a time: every
// kind wants a different subset, and threading them individually meant editing the chrome for
// each kind migrated.
interface ActionContext {
  action: WorkflowAction;
  workflow: Workflow;
  handlers: ActionHandlers;
}

// What a body renderer gets. No slideout state: a body renders inside the row and has no business
// knowing whether a sibling panel is open.
export interface ActionCardContext extends ActionContext {
  // Set by the facility-decision panel, whose rows render their own result.
  hideResult: boolean;
  // The generic completed surface, already null unless this step is completed.
  genericResult: ReactNode;
  // The generic option buttons and waiting copy, already null unless they apply.
  genericActions: ReactNode;
}

// A slideout renders as a sibling of the row, so it gets the open/close pair and none of the
// body's composition pieces.
export interface SlideoutContext extends ActionContext {
  slideoutOpen: boolean;
  onCloseSlideout: () => void;
}

export interface ActionCardEntry {
  // Whether this instance opens a slideout. A predicate rather than a flag because it depends on
  // the instance: a review surface only exists once there is a result to review.
  hasSlideout?: (action: WorkflowAction) => boolean;
  // Rendered as a SIBLING of the row, never inside it — the row is a button when it has a
  // slideout, and the slideout's own controls must not nest inside that button.
  slideout?: (ctx: SlideoutContext) => ReactNode;
  // This kind's entire body. Free-form on purpose: the kinds are genuinely irregular, and a
  // schema of named slots grew a slot every time one of them did not fit. Compose
  // ctx.genericResult / ctx.genericActions to keep the generic surfaces, or leave them out to
  // replace them. Omit `body` altogether and the kind gets both, which is most kinds.
  body?: (ctx: ActionCardContext) => ReactNode;
}

const completed = (action: WorkflowAction) => action.isCompleted;

export const ACTION_CARDS: Record<string, ActionCardEntry> = {
  DenialCheck: {
    hasSlideout: completed,
    slideout: ({ action, workflow, slideoutOpen, onCloseSlideout }) => (
      <DenialCheckReview
        action={action as DenialCheckAction}
        workflow={workflow}
        open={slideoutOpen}
        onClose={onCloseSlideout}
      />
    ),
    // In a multi-action category the row itself is the slideout trigger; this text-only summary is
    // the body for a lone single-action category. No genericResult: the slideout IS the surface.
    body: ({ action, genericActions }) => (
      <>
        {action.isCompleted && <DenialCheckSummary action={action as DenialCheckAction} />}
        {genericActions}
      </>
    ),
  },
  SexOffenderCheck: {
    hasSlideout: completed,
    slideout: ({ action, workflow, slideoutOpen, onCloseSlideout, handlers }) => (
      <SexOffenderCheckReview
        action={action as SexOffenderCheckAction}
        workflow={workflow}
        open={slideoutOpen}
        onClose={onCloseSlideout}
        onSaveProgress={handlers.onSaveProgress}
        isSavingProgress={handlers.isSavingProgress}
      />
    ),
    body: ({ action, genericActions }) => (
      <>
        {action.isCompleted && <SexOffenderCheckSummary action={action as SexOffenderCheckAction} />}
        {genericActions}
      </>
    ),
  },
  // Both of these complete with a packet/readout rather than a recorded value, so actionConfig
  // gives them no resultValueName and the generic CompletedResult renders nothing for them. That
  // is why these can own the completed body outright instead of rendering alongside it.
  ProcessAttachments: {
    // The note owns both faces of this step's completed body — a successful parse and a failed one
    // — plus the retry the failed face needs. See ProcessAttachmentsNote for why it reads the
    // PRESENTED status rather than `isCompleted`.
    body: ({ action, handlers: { onEdit, onInvoke, isInvoking }, genericActions }) => (
      <>
        {action.isCompleted && (
          <ProcessAttachmentsNote action={action} onEdit={onEdit} onInvoke={onInvoke} isInvoking={isInvoking} />
        )}
        {genericActions}
      </>
    ),
  },
  ExtractAttributes: {
    body: ({ action, workflow, handlers: { onEdit }, genericActions }) => (
      <>
        {action.isCompleted && (
          <>
            <CompletedNote>
              Review extracted patient demographics below.{' '}
              {onEdit && (
                <InlineLink type='button' onClick={() => onEdit('demographics')} data-testid='edit-demographics'>
                  Edit demographics
                </InlineLink>
              )}
            </CompletedNote>
            <ExtractionResults action={action} workflow={workflow} />
          </>
        )}
        {genericActions}
      </>
    ),
  },
  // The outcome is always recordable (OLIO-2611), so its panel stands in for the generic options
  // and waiting copy in both pending and action_required, and its own result replaces the generic
  // one. hideResult is honored because the facility-decision rows render their own result.
  AcceptReferral: {
    body: ({ action, workflow, handlers, hideResult }) => (
      <>
        {/* onUpdateValues is load-bearing, not decorative: ReferralOutcomeResult gates Change
            selected facility on it, so omitting it silently removes the only way to move a decided
            referral to another building before the EHR sync (OLIO-2846). onAmend is the same for
            the per-row Change to Selected / Not Selected menus (OLIO-2973) — without it the
            completed card offers no way to change the recorded outcome. */}
        {action.isCompleted && !hideResult && (
          <ReferralOutcomeResult
            action={action}
            workflow={workflow}
            onAmend={handlers.onAmend}
            isCompleting={handlers.isCompleting}
            onUpdateValues={handlers.onUpdateValues}
          />
        )}
        {(action.isPending || action.isActionRequired) && (
          <ReferralOutcomePanel
            action={action}
            workflow={workflow}
            onComplete={handlers.onComplete}
            isCompleting={handlers.isCompleting}
          />
        )}
      </>
    ),
  },
  // The remaining kinds add a panel to the generic surfaces rather than replacing them, which is
  // why they compose genericResult and genericActions around their own piece. The ordering is the
  // one they had inline: result, then the kind's panel, then the actions.
  DuplicatePatientSearch: {
    body: ({ action, handlers, genericResult, genericActions }) => (
      <>
        {genericResult}
        {action.isActionRequired && (
          <DuplicatePatientReview
            action={action}
            onComplete={handlers.onComplete}
            isCompleting={handlers.isCompleting}
          />
        )}
        {action.isCompleted && <DuplicateLinkNote action={action} />}
        {genericActions}
      </>
    ),
  },
  // Landed on master after this registry did (OLIO-2846). Owns every state it renders in — running,
  // completed, errored and action_required, which is its ready-to-send state — so it is not gated on
  // one of them here. It returns null while `pending`, which under design 010 means the referral is
  // undecided, and the definition's own description renders instead via genericActions.
  SyncPatientToEhr: {
    body: ({ action, workflow, handlers, genericResult, genericActions }) => (
      <>
        {genericResult}
        <SyncPatientBody
          action={action as SyncPatientToEhrAction}
          workflow={workflow}
          onDispatch={handlers.onDispatch}
          onResend={handlers.onResend}
          isDispatching={handlers.isDispatching}
        />
        {genericActions}
      </>
    ),
  },
};

export function actionCardFor(action: WorkflowAction): ActionCardEntry {
  return ACTION_CARDS[action.kind] ?? {};
}

export function hasSlideoutFor(action: WorkflowAction): boolean {
  return actionCardFor(action).hasSlideout?.(action) ?? false;
}
